/**
 * Attorney Questions — the agent surface for raising a question that needs a licensed
 * attorney's answer (slice 2 of attorney question routing).
 *
 * SHIPS DARK. A submitted question lands on "Waiting to send" and stops there: nothing routes
 * it, emails an attorney, or answers it yet. The page says so plainly, because an agent who
 * believes a member's legal question is on its way when it is not is worse than no page at all.
 *
 * Every rule here is also enforced server-side by create_attorney_question. The client-side
 * checks exist to give a better message, not to be the gate.
 *
 * See docs/DESIGN_ATTORNEY_QUESTION_ROUTING.md
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../components/ui/Toast";
import { featureFlags } from "../utils/featureFlags";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  searchContacts,
  type ContactPickerResult,
} from "../services/contacts.service";
import {
  contactPickerCanSearch,
  contactPickerStatus,
} from "../utils/contactPickerStatus";
import {
  ATTORNEY_TASK_TYPES,
  STAGE_LABELS,
  createAttorneyQuestion,
  listAttorneyQuestions,
  listCoveredStates,
  type AttorneyQuestionRow,
  type AttorneyTaskType,
} from "../services/attorneyQuestions.service";

/**
 * Dark-flag gate. Returns BEFORE any query is declared, so a flagged-off environment issues
 * no RPCs at all rather than rendering an empty shell that still talks to the database.
 */
function FeatureOffNotice() {
  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
        <strong className="block text-gray-900 mb-1">Not available yet</strong>
        Attorney question routing is still being built and is not switched on in
        this environment.
      </div>
    </div>
  );
}

const AttorneyQuestionsInner: React.FC = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const searchRef = useRef<HTMLDivElement>(null);

  const [showForm, setShowForm] = useState(false);
  const [contact, setContact] = useState<ContactPickerResult | null>(null);
  const [contactSearch, setContactSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [taskType, setTaskType] =
    useState<AttorneyTaskType>("attorney_required");
  const [states, setStates] = useState<string[]>([]);
  const [questionText, setQuestionText] = useState("");
  const [saving, setSaving] = useState(false);

  const questionsQ = useQuery({
    queryKey: ["attorney-questions"],
    queryFn: () => listAttorneyQuestions(100),
  });
  const coveredQ = useQuery({
    queryKey: ["attorney-covered-states"],
    queryFn: listCoveredStates,
  });
  const {
    data: contactResults = [],
    isFetching: contactSearching,
    isError: contactSearchFailed,
  } = useQuery({
    queryKey: ["contact-search", contactSearch],
    queryFn: () => searchContacts(contactSearch),
    enabled: contactPickerCanSearch(contactSearch),
    // One retry only: a failing search should say so, not spin for ~7 s.
    retry: 1,
  });

  const rows: AttorneyQuestionRow[] = questionsQ.data ?? [];
  const coveredStates = coveredQ.data ?? [];
  const typeMeta = useMemo(
    () => ATTORNEY_TASK_TYPES.find((t) => t.value === taskType)!,
    [taskType],
  );

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Switching to a type that takes a different number of states must not silently carry a
  // now-invalid selection into the submission.
  useEffect(() => {
    setStates((prev) => {
      if (typeMeta.states === "none") {
        return [];
      }
      if (typeMeta.states === "one") {
        return prev.slice(0, 1);
      }
      return prev;
    });
  }, [typeMeta.states]);

  const resetForm = () => {
    setShowForm(false);
    setContact(null);
    setContactSearch("");
    setTaskType("attorney_required");
    setStates([]);
    setQuestionText("");
  };

  const toggleState = (code: string) => {
    setStates((prev) => {
      if (typeMeta.states === "one") {
        return prev[0] === code ? [] : [code];
      }
      return prev.includes(code)
        ? prev.filter((s) => s !== code)
        : [...prev, code];
    });
  };

  const handleSubmit = async () => {
    if (!contact) {
      showToast("error", "Choose the member this question is about.");
      return;
    }
    if (!questionText.trim()) {
      showToast("error", "Enter the member's question.");
      return;
    }
    if (typeMeta.states === "one" && states.length !== 1) {
      showToast("error", "Choose exactly one state.");
      return;
    }
    if (typeMeta.states === "many" && states.length < 1) {
      showToast("error", "Choose at least one state.");
      return;
    }
    setSaving(true);
    try {
      const res = await createAttorneyQuestion({
        contact_id: contact.id,
        task_type: taskType,
        question_text: questionText,
        states,
      });
      showToast("success", `Question #${res.task_number} raised.`);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["attorney-questions"] });
    } catch (e) {
      showToast("error", (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const contactLabel = (c: ContactPickerResult) =>
    [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || c.id;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Attorney Questions
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Member questions that need a licensed attorney's answer.
          </p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} variant="primary">
            Raise a question
          </Button>
        )}
      </div>

      <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
        <strong>Not live yet.</strong> Questions raised here are recorded but
        are not yet sent to an attorney. Keep handling these the way you do
        today — this page is being built and is here for your feedback.
      </div>

      {questionsQ.error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {(questionsQ.error as Error).message}
        </div>
      )}

      {showForm && (
        <div className="mb-6 rounded-lg border border-gray-300 bg-white p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Raise a question
          </h2>

          <div className="mb-4" ref={searchRef}>
            <label
              htmlFor="aq-member"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Member
            </label>
            {contact ? (
              <div className="flex items-center gap-3 border border-gray-300 rounded-md px-3 py-2">
                <span className="text-sm">{contactLabel(contact)}</span>
                <span className="text-xs text-gray-400">{contact.email}</span>
                <button
                  type="button"
                  className="ml-auto text-sm text-blue-600 hover:underline"
                  onClick={() => setContact(null)}
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  id="aq-member"
                  type="text"
                  value={contactSearch}
                  placeholder="Search by name, email or membership number…"
                  onChange={(e) => {
                    setContactSearch(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  className="w-full border border-gray-300 rounded-md text-sm py-2 px-2"
                />
                {showDropdown && contactSearch.trim().length > 0 && (
                  <ul
                    data-testid="aq-member-results"
                    className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow"
                  >
                    {contactResults.length === 0 ? (
                      <li className="px-3 py-2 text-sm text-gray-500">
                        {contactPickerStatus(
                          contactSearch,
                          contactSearching,
                          contactSearchFailed,
                        )}
                      </li>
                    ) : (
                      contactResults.slice(0, 10).map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                            onClick={() => {
                              setContact(c);
                              setShowDropdown(false);
                            }}
                          >
                            {contactLabel(c)}
                            <span className="ml-2 text-xs text-gray-400">
                              {c.email}
                            </span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="mb-4">
            <label
              htmlFor="aq-type"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Question type
            </label>
            <select
              id="aq-type"
              value={taskType}
              onChange={(e) => setTaskType(e.target.value as AttorneyTaskType)}
              className="w-full md:w-96 border border-gray-300 rounded-md text-sm py-2 px-2 bg-white"
            >
              {ATTORNEY_TASK_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">{typeMeta.help}</p>
          </div>

          {typeMeta.states !== "none" && (
            <div className="mb-4">
              <span className="block text-sm font-medium text-gray-700 mb-1">
                {typeMeta.states === "one" ? "State" : "States involved"}
              </span>
              {coveredStates.length === 0 ? (
                <div className="rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                  No states have an attorney assigned yet, so a question cannot
                  be raised. Ask an admin to set attorney coverage first.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {coveredStates.map((code) => {
                    const on = states.includes(code);
                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => toggleState(code)}
                        className={`px-3 py-1 rounded-full border text-sm ${
                          on
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "bg-white border-gray-300 text-gray-700 hover:border-blue-400"
                        }`}
                      >
                        {code}
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Only states with an attorney assigned can be chosen.
              </p>
            </div>
          )}

          <div className="mb-4">
            <label
              htmlFor="aq-text"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              The member's question
            </label>
            <textarea
              id="aq-text"
              rows={4}
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="What did the member ask? Include anything the attorney needs to answer it."
              className="w-full border border-gray-300 rounded-md text-sm py-2 px-2"
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSubmit} variant="primary" disabled={saving}>
              {saving ? "Submitting…" : "Submit question"}
            </Button>
            <Button onClick={resetForm} variant="secondary" disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {questionsQ.isLoading ? (
        <LoadingSpinner />
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
          No attorney questions yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  #
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Member
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Question
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  States
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Status
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  Raised
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((r) => (
                <tr key={r.support_task_id}>
                  <td className="px-4 py-2 font-medium">{r.task_number}</td>
                  <td className="px-4 py-2">
                    <div>{r.member_name || "—"}</div>
                    <div className="text-xs text-gray-400">
                      {r.membership_id}
                    </div>
                  </td>
                  <td className="px-4 py-2 max-w-md">
                    <div className="truncate" title={r.question_text}>
                      {r.question_text}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {r.states.length ? r.states.join(", ") : "—"}
                  </td>
                  <td className="px-4 py-2">
                    <span className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                      {STAGE_LABELS[r.stage] ?? r.stage}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {new Date(r.created_at).toLocaleDateString()}
                    {r.created_by_name ? ` · ${r.created_by_name}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AttorneyQuestions: React.FC = () =>
  featureFlags.attorneyQuestionRouting ? (
    <AttorneyQuestionsInner />
  ) : (
    <FeatureOffNotice />
  );

export default AttorneyQuestions;
